/** Towns nobody starts with, and what a town is defended by.
 *
 * The neutral-town pass itself, the neutral build list and garrison that
 * scale with distance from the nearest player, and `startingGarrison` - the
 * capital's day-one defence, which lives here because it is sized against the
 * same `baseTiers` ladder.
 */

import type { ArmySlot, BuildingDefId, ContentRegistry, CreatureDef, FactionId } from "@game/core";
import { startingBuildings } from "@game/core";
import { idx } from "./grid.js";
import { dangerBand, nothing, place } from "./placement.js";
import { pick, shuffle } from "./rng.js";
import { findTownSite, reserveAround, townDefFor, townFactions } from "./siting.js";
import type { GenState } from "./state.js";
import { clamp } from "./state.js";
import type { GeneratedTown } from "./types.js";

/**
 * Unowned towns in the neutral zones. This is the object that turns the middle
 * of a classic map from scenery into territory: it is worth more than any bank,
 * it is permanent, and taking it is a campaign rather than a fight. Their
 * defence therefore has to scale with how far into no-man's-land they sit -
 * one on a player's doorstep that a week-one hero can walk into would decide
 * the game instead of shaping it.
 *
 * The town object is placed and the garrison and build list are *returned*
 * rather than applied: `newGame` builds a `TownState` only from a
 * `PlayerSetup`, so an unowned town cannot be created by this package today.
 * `TownSetup.garrison` and `TownSetup.built` are exactly the two fields
 * `GeneratedTown` now carries, so the day core grows an owner-less town setup,
 * the caller has the arguments already.
 */
export function placeNeutralTowns(
  s: GenState, count: number, zoneOf: Int32Array, towns: GeneratedTown[],
  reserved: Uint8Array, z: number, minZone: number,
): void {
  if (count <= 0) return;
  const factions = townFactions(s.content);
  if (factions.length === 0) return;

  const candidates = shuffle(s.r, s.zones
    .filter((zn) => zn.job !== "starting" && zn.job !== "water" && zn.cells.length >= minZone)
    .slice())
    .sort((a, b) => {
      const rank = (j: typeof a.job): number =>
        j === "junction" ? 0 : j === "treasure" ? 1 : 2;
      return rank(a.job) - rank(b.job);
    });

  let placed = 0;
  for (const zone of candidates) {
    if (placed >= count) break;
    const site = findTownSite(s.map, s.r, zone, zoneOf);
    if (site === null) continue;
    const faction = pick(s.r, factions);
    if (faction === null) continue;
    const defId = townDefFor(s.content, faction);
    const at = place(s, [idx(s.g, site.x, site.y)], defId, nothing, 1);
    if (at < 0) continue;
    reserveAround(reserved, s.g, site.entX, site.entY, 1);
    const band = dangerBand(s, idx(s.g, site.entX, site.entY));
    towns.push({
      player: -1, faction, def: defId,
      x: site.entX, y: site.entY, z, object: at,
      garrison: neutralGarrison(s.content, faction, band),
      built: neutralBuildings(s.content, faction, band),
    });
    placed++;
  }
}

/** What every neutral town has before its distance earns it any more. */
const NEUTRAL_TOWN_SLOTS: readonly string[] = Object.freeze([
  "hall", "fort", "tavern", "dwelling_1",
]);

/**
 * A fort, a hall, a tavern and as much of the dwelling ladder as the town's
 * distance from the nearest player buys. The fort is the point: it is what
 * makes taking the town a siege and holding it worth doing.
 */
function neutralBuildings(
  content: ContentRegistry, faction: FactionId, band: number,
): BuildingDefId[] {
  const slots = [...NEUTRAL_TOWN_SLOTS];
  for (let k = 2; k <= 1 + band && k <= 7; k++) slots.push(`dwelling_${String(k)}`);
  return startingBuildings(content, faction, slots);
}

/** The unupgraded creature of each tier a faction has, lowest id wins a tie. */
function baseTiers(
  content: ContentRegistry, faction: FactionId,
): Map<number, CreatureDef> {
  const base = new Map<number, CreatureDef>();
  const roster = [...content.creatures.values()]
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const c of roster) {
    if (c.faction !== faction || c.upgraded) continue;
    if (!base.has(c.tier)) base.set(c.tier, c);
  }
  return base;
}

/**
 * What a capital is defended by on day one, and why by that much.
 *
 * The classic leaves a random-map capital empty and gets away with it because the
 * corridor between two starts is walled with guarded gateways. This generator
 * guards its ridge gates, but it also lays a trunk road from capital to
 * capital, and the road walks past them. Measured with the garrison empty:
 * three of five headless games ended on days 3, 4 and 6, each one a hero
 * walking through an open door rather than a war anybody fought.
 *
 * The size is set in aiValue points rather than in creatures, because a count
 * that is a fair fight in front of Castle's 80-point Spearwardens is a massacre in
 * front of Prismgate's 356-point Gale Spirits; normalising is what stops the
 * faction roll deciding who can be rushed. `START_GARRISON_VALUE` is 3600, and
 * that number is three comparisons:
 *
 *  - **more than a starting hero's own army, and less than two of them.** A
 *    starting hero opens with 2000 to 4500 points, measured across the ten
 *    seeds of the 36x36 headline; the mean is near 3000. So a hero who marches
 *    on day one and attacks into a Fort loses, and loses the only army his own
 *    capital had to be defended with - and *two* heroes arriving on consecutive
 *    days can still take the hall, which is a war rather than a wall.
 *
 *    2400 was the round-9 figure and it was set against a hero's army alone.
 *    The measurement that moved it is that a garrison has no hero: nothing in
 *    it carries an attack or defence bonus and nothing in it casts, so it
 *    fights well below its own aiValue. On 36x36 seed 16838 a garrison of 5082
 *    lost to a hero carrying 3414 with the walls still standing. Parity in
 *    points is not parity on the field, and the defence has to be bought at the
 *    price the field charges.
 *  - **under two weeks of the two dwellings a capital opens with.** `newGame`
 *    builds `dwelling_1` and `dwelling_2`, so a player who spends the first
 *    fortnight recruiting rather than marching can take a capital in week
 *    three. That is the game this is meant to be: an opening you have to
 *    survive, not a door.
 *  - **well under what a month buys.** A month of recruiting plus one cleared
 *    bank is comfortably past 3600, so the garrison decays into a speed bump
 *    rather than becoming a wall. A defence that cannot be beaten makes the
 *    town scenery, which is the same defect facing the other way. Measured up
 *    as far as 4800 and it is past the top of the useful range: the 36x36
 *    headline came back with a two-day game and the 48x48 four-player one fell
 *    to 6 of 10, because a hall nobody can take early is a hall every computer
 *    walks past on its way to a longer war.
 *
 * Tiers 1 and 2 only, split 45/55 by value. Those are exactly the two dwellings
 * the town opens with, so the defence is what the town itself makes - a capital
 * held by creatures its owner could not have recruited reads as a gift from the
 * scenario rather than as a garrison. Counts are clamped to between half a week
 * and three weeks of that creature's own growth, so the normalisation can never
 * produce three Gale Spirits or a hundred Tinkerlings.
 *
 * The clamp and the split are what keep the normalisation honest at this size,
 * and both moved with it. At 2400 no clamp ever bound; at 3600 under the old
 * two-week ceiling and 55/45 split, five of the twelve factions could not spend
 * their tier-1 budget and shipped 83% to 89% of the target - Tower 3058,
 * Gravewatch 3008, Fenmire 2982 - while Greenmantle shipped 3656. That is the
 * faction roll deciding who is rushable, which is the defect the value
 * normalisation exists to prevent, reappearing through the ceiling. Three weeks
 * and 45/55 puts every one of the twelve inside 97% to 104%.
 */
export const START_GARRISON_VALUE = 3000;
const START_GARRISON_TIER1_BP = 4500;
/** Weeks of its own growth a single starting-garrison stack may be worth. */
export const START_GARRISON_WEEKS = 3;

export function startingGarrison(content: ContentRegistry, faction: FactionId): ArmySlot[] {
  const base = baseTiers(content, faction);
  const shareBp = [START_GARRISON_TIER1_BP, 10000 - START_GARRISON_TIER1_BP];
  const out: ArmySlot[] = [];
  for (let tier = 1; tier <= 2; tier++) {
    const def = base.get(tier);
    if (def === undefined) continue;
    const budget = (START_GARRISON_VALUE * shareBp[tier - 1]!) / 10000;
    const count = clamp(
      Math.round(budget / Math.max(1, def.aiValue)),
      Math.max(1, Math.round(def.growth / 2)),
      def.growth * START_GARRISON_WEEKS);
    out.push({ def: def.id, count });
  }
  return out;
}

/**
 * The standing garrison, one week of each dwelling's growth per danger band.
 * The top two tiers are halved: seven weeks of Umbral Wyrms is not a garrison,
 * it is a wall, and a wall makes the town scenery again.
 */
function neutralGarrison(
  content: ContentRegistry, faction: FactionId, band: number,
): ArmySlot[] {
  const base = baseTiers(content, faction);
  const top = clamp(2 + band, 2, 7);
  const out: ArmySlot[] = [];
  for (let tier = 1; tier <= top; tier++) {
    const def = base.get(tier);
    if (def === undefined) continue;
    const count = Math.max(1, Math.round((def.growth * (1 + band)) / (tier >= 6 ? 2 : 1)));
    out.push({ def: def.id, count });
  }
  return out;
}
